import { Reveal, Overline } from "./Reveal";
import { Check, MoveHorizontal } from "lucide-react";

const tiers = [
  {
    name: "Signature",
    tagline: "The essential membership",
    monthlyInr: "₹16,667",
    annualInr: "₹2,00,000",
    monthlyUsd: "$200",
    annualUsd: "$2,400",
    features: [
      "Dedicated lifestyle manager",
      "Restaurant & event reservations",
      "Travel & aviation coordination",
      "Priority global procurement",
      "15-hour service",
    ],
    dark: false,
  },
  {
    name: "Noir",
    tagline: "By invitation only",
    monthlyInr: "₹25,000",
    annualInr: "₹3,00,000",
    monthlyUsd: "$300",
    annualUsd: "$3,600",
    features: [
      "Everything in Signature",
      "Two dedicated managers",
      "Unrestricted global access & VIP guest lists",
      "Bespoke procurement without limits",
      "24/7 immediate response",
    ],
    dark: true,
  },
];

const TierCard = ({ t, onSelect }) => (
  <div
    data-testid={`tier-card-${t.name.toLowerCase()}`}
    className={`snap-center shrink-0 w-[85vw] sm:w-[420px] md:w-[500px] flex flex-col p-8 md:p-12 border ${
      t.dark
        ? "bg-navy text-ivory border-gold/30"
        : "bg-ivory text-navy border-navy/10"
    }`}
  >
    <div className="flex items-baseline justify-between">
      <h3 className="font-serif font-light text-3xl md:text-4xl tracking-tight">
        {t.name}
      </h3>
      {t.dark && (
        <span className="text-[0.6rem] tracking-[0.2em] uppercase text-gold border border-gold/50 rounded-full px-3 py-1">
          Invite Only
        </span>
      )}
    </div>
    <p
      className={`mt-2 font-sans font-light text-sm ${
        t.dark ? "text-ivory/70" : "text-navyAccent/70"
      }`}
    >
      {t.tagline}
    </p>

    {/* Pricing */}
    <div
      className={`mt-8 pb-8 border-b ${
        t.dark ? "border-ivory/15" : "border-navy/10"
      }`}
    >
      <div className="flex items-baseline gap-2">
        <span className="font-serif text-4xl md:text-5xl leading-none">
          {t.monthlyInr}
        </span>
        <span
          className={`font-sans font-light text-sm ${
            t.dark ? "text-ivory/60" : "text-navyAccent/60"
          }`}
        >
          / month
        </span>
      </div>
      <p
        className={`mt-3 font-sans font-light text-sm ${
          t.dark ? "text-ivory/70" : "text-navyAccent/70"
        }`}
      >
        Billed annually at{" "}
        <span className={t.dark ? "text-gold" : "text-navy font-normal"}>
          {t.annualInr}
        </span>
      </p>
      <p
        className={`mt-1 font-sans font-light text-xs tracking-wide ${
          t.dark ? "text-ivory/45" : "text-navyAccent/50"
        }`}
      >
        Approx. {t.monthlyUsd}/mo · {t.annualUsd} billed annually
      </p>
    </div>

    <ul className="mt-8 space-y-4 flex-1">
      {t.features.map((f) => (
        <li key={f} className="flex items-start gap-3">
          <Check size={16} className="text-gold mt-1 shrink-0" strokeWidth={1.5} />
          <span
            className={`font-sans font-light text-sm leading-relaxed ${
              t.dark ? "text-ivory/90" : "text-navyAccent"
            }`}
          >
            {f}
          </span>
        </li>
      ))}
    </ul>

    <a
      href="#apply"
      onClick={() => onSelect && onSelect(t.name)}
      data-testid={`tier-select-${t.name.toLowerCase()}`}
      className={`mt-10 inline-flex items-center justify-center rounded-full px-6 py-3.5 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
        t.dark
          ? "bg-gold text-navy hover:bg-goldSubtle"
          : "border border-navy/30 text-navy hover:border-gold hover:text-gold"
      }`}
    >
      Apply for {t.name}
    </a>
  </div>
);

export const Tiers = ({ onSelect }) => {
  return (
    <section
      id="membership"
      data-testid="tiers-section"
      className="relative bg-beige py-28 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-12 md:mb-16">
          <Reveal>
            <Overline>Membership</Overline>
            <h2 className="mt-6 font-serif font-light text-navy text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Two thresholds.
              <br />
              <span className="italic text-gold">One standard.</span>
            </h2>
            <p className="mt-6 font-sans font-light text-navyAccent/80 text-base leading-relaxed">
              Membership is limited and offered strictly by invitation. Proposed
              monthly, charged on an annual basis. Select a tier to begin your
              private application.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="flex items-center gap-2 mb-6 text-navyAccent/50">
            <MoveHorizontal size={16} className="text-gold" />
            <span className="text-[0.65rem] tracking-[0.2em] uppercase font-sans">
              Scroll to compare
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            data-testid="tiers-scroller"
            className="flex gap-6 md:gap-8 overflow-x-auto snap-x snap-mandatory pb-6 -mx-6 px-6 md:-mx-12 md:px-12 scroll-smooth"
            style={{ scrollbarWidth: "thin" }}
          >
            {tiers.map((t) => (
              <TierCard key={t.name} t={t} onSelect={onSelect} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
